import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  SlidersHorizontal,
  Plus,
  Pencil,
  Trash2,
  X,
  Loader2,
  Save,
  Search,
  Filter,
  AlertTriangle,
  Tag,
  Layers,
  CalendarDays,
  RefreshCw,
  CheckCircle2,
  Power,
  ListFilter,
} from 'lucide-react';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import { useFormController } from '../../Shared/Component/readFormData/useFormController';
import {
  initialSearchConfig,
  searchConfigRequest,
  searchConfigResponse,
} from '../Types/AdminType';
import { createSearchConfig, deleteSearchConfig, updateSearchConfig } from '../Services/AdminService';

interface Props {
  data: searchConfigResponse[];
  onEditSubmit: (data: searchConfigResponse) => void;
  onDeleteConfirm: (fieldName: string) => void;
  onRegisterSubmit: (data: Partial<searchConfigResponse>) => void;
  menuName: string;
  onChanged?: () => void;
}

const FILTER_TYPE_OPTIONS = [
  { value: 'TEXT', label: 'Text' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'BOOLEAN', label: 'Boolean' },
  { value: 'MULTI', label: 'Multi' },
  { value: 'RANGE', label: 'Range' },
  { value: 'GT', label: 'Greater Than' },
  { value: 'GTE', label: 'Greater Than & Equal' },
  { value: 'LT', label: 'Less Than' },
  { value: 'LTE', label: 'Less Than & Equal' },
];

const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-200 placeholder-slate-600 outline-none transition [color-scheme:dark] focus:border-cyan-500/50';

const labelClass = 'mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-400';

const toNumber = (value: unknown): number => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const prettify = (value?: string) =>
  value ? value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : '—';

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const boolMeta = (value: boolean, yes: string, no: string) => ({
  text: value ? yes : no,
  cls: value
    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
    : 'border-rose-500/30 bg-rose-500/10 text-rose-400',
});

const DarkField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className={labelClass}>{label}</label>
    {children}
  </div>
);

const StatCard = ({
  label,
  value,
  icon,
  accent,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  accent: string;
}) => (
  <div className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
    <div className="flex items-center justify-between">
      <span className={`flex h-9 w-9 items-center justify-center rounded-xl border ${accent}`}>{icon}</span>
      <span className="text-2xl font-black text-slate-100">{value}</span>
    </div>
    <p className="mt-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
  </div>
);

const SearchFilterConfigComponent = ({
  data,
  onEditSubmit,
  onDeleteConfirm,
  onRegisterSubmit,
  menuName,
  onChanged,
}: Props) => {
  const { values, setAllValues, handleInputChange, handleValueChange } =
    useFormController<searchConfigRequest>(initialSearchConfig);

  const [selectedConfig, setSelectedConfig] = useState<searchConfigResponse | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<searchConfigResponse | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [alert, setAlert] = useState<AlertProps | null>(null);

  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const rows = data ?? [];

  const tierOptions = useMemo(
    () => Array.from(new Set(rows.map((row) => row.tier).filter(Boolean))).sort(),
    [rows]
  );

  const stats = useMemo(
    () => ({
      total: rows.length,
      active: rows.filter((row) => row.enabled).length,
      inactive: rows.filter((row) => !row.enabled).length,
      tiers: tierOptions.length,
    }),
    [rows, tierOptions]
  );

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesSearch =
        !term ||
        row.fieldName?.toLowerCase().includes(term) ||
        row.label?.toLowerCase().includes(term) ||
        row.tier?.toLowerCase().includes(term);

      const matchesTier = tierFilter === 'ALL' || row.tier === tierFilter;
      const matchesType = typeFilter === 'ALL' || row.filterType === typeFilter;
      const matchesStatus =
        statusFilter === 'ALL' ||
        (statusFilter === 'ACTIVE' && row.enabled) ||
        (statusFilter === 'INACTIVE' && !row.enabled);

      return matchesSearch && matchesTier && matchesType && matchesStatus;
    });
  }, [rows, search, tierFilter, typeFilter, statusFilter]);

  const hasActiveFilters =
    Boolean(search) || tierFilter !== 'ALL' || typeFilter !== 'ALL' || statusFilter !== 'ALL';

  const resetFilters = () => {
    setSearch('');
    setTierFilter('ALL');
    setTypeFilter('ALL');
    setStatusFilter('ALL');
  };

  const buildPayload = (): searchConfigRequest => ({
    fieldName: values.fieldName,
    label: values.label,
    filterType: values.filterType,
    tier: values.tier,
    enabled: values.enabled,
    displayOrder: toNumber(values.displayOrder),
  });

  const openCreate = () => {
    setAllValues(initialSearchConfig);
    setSelectedConfig(null);
    setFormMode('create');
    setFormOpen(true);
  };

  const openEdit = (config: searchConfigResponse) => {
    setAllValues({
      fieldName: config.fieldName || '',
      label: config.label || '',
      filterType: config.filterType || '',
      tier: config.tier || '',
      enabled: config.enabled,
      displayOrder: config.displayOrder || 0,
    });
    setSelectedConfig(config);
    setFormMode('edit');
    setFormOpen(true);
  };

  const handleSubmit = async () => {
    if (!values.fieldName.trim()) {
      setAlert({
        variant: 'error',
        title: 'Validation',
        message: 'Field name is required.',
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
      return;
    }

    setSaving(true);
    setAlert(null);
    try {
      const payload = buildPayload();
      const response =
        formMode === 'create'
          ? await createSearchConfig(payload)
          : await updateSearchConfig(payload, selectedConfig?.id || '');

      if (response.success) {
        setAlert({
          variant: 'success',
          title: 'Success',
          message:
            response.data?.fieldName
              ? `${response.data.fieldName} has been ${
                  formMode === 'create' ? 'added' : 'updated'
                } successfully.`
              : `${menuName} ${formMode === 'create' ? 'created' : 'updated'} successfully.`,
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
        setFormOpen(false);
        onChanged?.();
        if (formMode === 'create') {
          onRegisterSubmit({});
        } else if (selectedConfig) {
          onEditSubmit(selectedConfig);
        }
      } else {
        setAlert({
          variant: 'error',
          title: 'Error',
          message: response.message || `Could not save the ${menuName}.`,
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      }
    } catch (err) {
      console.error('Failed to save search config', err);
      setAlert({
        variant: 'error',
        title: 'System error',
        message: 'Something went wrong. Please try again.',
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setAlert(null);
    try {
      const response = await deleteSearchConfig(deleteTarget.id);
      if (response.success) {
        setAlert({
          variant: 'success',
          title: 'Deleted',
          message: `${deleteTarget.fieldName} has been deleted successfully.`,
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
        onDeleteConfirm(deleteTarget.fieldName);
        setDeleteTarget(null);
        onChanged?.();
      } else {
        setAlert({
          variant: 'error',
          title: 'Delete Error',
          message: response.message || `Could not delete the ${menuName}.`,
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      }
    } catch (err) {
      console.error('Failed to delete search config', err);
      setAlert({
        variant: 'error',
        title: 'System error',
        message: 'Something went wrong. Please try again.',
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      {alert && <Alert {...alert} />}

      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
            <SlidersHorizontal size={22} />
          </span>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">{menuName}</h1>
            <p className="text-xs font-semibold text-slate-400 md:text-sm">
              Configure the filters available across the platform search
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => onChanged?.()}
            className="flex items-center justify-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40"
          >
            <RefreshCw size={15} /> Refresh
          </button>
          <button
            onClick={openCreate}
            className="flex items-center justify-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-400"
          >
            <Plus size={15} /> Add {menuName}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Total Fields"
          value={stats.total}
          icon={<SlidersHorizontal size={16} />}
          accent="border-cyan-500/30 bg-cyan-500/10 text-cyan-400"
        />
        <StatCard
          label="Active"
          value={stats.active}
          icon={<CheckCircle2 size={16} />}
          accent="border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
        />
        <StatCard
          label="Inactive"
          value={stats.inactive}
          icon={<Power size={16} />}
          accent="border-rose-500/30 bg-rose-500/10 text-rose-400"
        />
        <StatCard
          label="Tiers"
          value={stats.tiers}
          icon={<Layers size={16} />}
          accent="border-violet-500/30 bg-violet-500/10 text-violet-400"
        />
      </div>

      {/* Filters */}
      <div className="mb-6 rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
        <div className="mb-3 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-400">
          <Filter size={14} /> Filters
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <div className="relative xl:col-span-2">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by field name, label or tier..."
              className="w-full rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-9 text-sm text-slate-200 placeholder-slate-600 outline-none transition focus:border-cyan-500/50"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                <X size={15} />
              </button>
            )}
          </div>

          <div className="relative">
            <Layers size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <select
              value={tierFilter}
              onChange={(e) => setTierFilter(e.target.value)}
              className="w-full appearance-none rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-8 text-sm text-slate-200 outline-none transition [color-scheme:dark] focus:border-cyan-500/50"
            >
              <option value="ALL" className="bg-slate-900 text-slate-100">
                All Tiers
              </option>
              {tierOptions.map((tier) => (
                <option key={tier} value={tier} className="bg-slate-900 text-slate-100">
                  {tier}
                </option>
              ))}
            </select>
          </div>

          <div className="relative">
            <ListFilter size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full appearance-none rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-8 text-sm text-slate-200 outline-none transition [color-scheme:dark] focus:border-cyan-500/50"
            >
              <option value="ALL" className="bg-slate-900 text-slate-100">
                All Types
              </option>
              {FILTER_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value} className="bg-slate-900 text-slate-100">
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3">
          {[
            { label: 'All Status', value: 'ALL' },
            { label: 'Active', value: 'ACTIVE' },
            { label: 'Inactive', value: 'INACTIVE' },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => setStatusFilter(option.value)}
              className={`rounded-full border px-3 py-1.5 text-[10px] font-black uppercase tracking-widest transition ${
                statusFilter === option.value
                  ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-300'
                  : 'border-slate-800 bg-slate-950 text-slate-400 hover:text-slate-200'
              }`}
            >
              {option.label}
            </button>
          ))}

          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="ml-auto flex items-center gap-1.5 rounded-full border border-slate-800 bg-slate-950 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-rose-400 hover:border-rose-500/40"
            >
              <X size={12} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/30 py-20 text-center">
          <span className="mb-2 block text-2xl">🧩</span>
          <p className="text-xs font-black uppercase tracking-widest text-slate-400">No search fields found</p>
          <button onClick={openCreate} className="mt-3 text-xs font-bold text-cyan-400 hover:underline">
            Create your first {menuName}
          </button>
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/30 py-20 text-center">
          <span className="mb-2 block text-2xl">🔍</span>
          <p className="text-xs font-black uppercase tracking-widest text-slate-400">No matching fields</p>
          <button onClick={resetFilters} className="mt-3 text-xs font-bold text-cyan-400 hover:underline">
            Clear filters
          </button>
        </div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/40">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 bg-slate-900/70 px-5 py-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-800 bg-slate-950 text-cyan-400">
                  <SlidersHorizontal size={18} />
                </span>
                <div>
                  <h3 className="text-sm font-black tracking-tight text-slate-100 md:text-base">{menuName} Directory</h3>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                    {filteredRows.length} item{filteredRows.length === 1 ? '' : 's'}
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[960px] border-collapse text-left">
                <thead>
                  <tr className="border-b border-slate-800 text-[10px] font-black uppercase tracking-widest text-slate-500">
                    <th className="px-5 py-3">Field</th>
                    <th className="px-5 py-3">Tier</th>
                    <th className="px-5 py-3">Filter Type</th>
                    <th className="px-5 py-3">Display Order</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3">Created</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((config) => {
                    const status = boolMeta(config.enabled, 'Active', 'Inactive');
                    return (
                      <tr
                        key={config.id}
                        className="border-b border-slate-800/60 transition last:border-0 hover:bg-slate-800/20"
                      >
                        <td className="px-5 py-4">
                          <p className="text-sm font-bold text-slate-100">{config.fieldName || '—'}</p>
                          {config.label && (
                            <p className="mt-0.5 text-[11px] font-semibold text-slate-500">{config.label}</p>
                          )}
                        </td>

                        <td className="px-5 py-4">
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 bg-slate-950 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-slate-300">
                            <Layers size={11} className="text-slate-500" />
                            {config.tier || '—'}
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-300">
                            <Tag size={12} className="text-slate-500" />
                            {prettify(config.filterType)}
                          </span>
                        </td>

                        <td className="px-5 py-4 text-xs font-semibold text-slate-300">
                          {config.displayOrder ?? 0}
                        </td>

                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${status.cls}`}
                          >
                            <span className="h-1.5 w-1.5 rounded-full bg-current" />
                            {status.text}
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-300">
                            <CalendarDays size={12} className="text-slate-500" />
                            {formatDate(config.createdAt)}
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => openEdit(config)}
                              title="Edit field"
                              className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40"
                            >
                              <Pencil size={12} /> Edit
                            </button>
                            <button
                              onClick={() => setDeleteTarget(config)}
                              className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-rose-400 transition hover:border-rose-500/40"
                            >
                              <Trash2 size={12} /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      )}

      {/* Create / Edit modal */}
      <AnimatePresence>
        {formOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/70 p-4 backdrop-blur-sm"
            onClick={() => !saving && setFormOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              onClick={(e) => e.stopPropagation()}
              className="my-8 w-full max-w-2xl rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl"
            >
              <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
                    <SlidersHorizontal size={18} />
                  </span>
                  <div>
                    <h2 className="text-base font-black tracking-tight text-slate-100">
                      {formMode === 'create' ? `Create New ${menuName}` : `Edit ${selectedConfig?.fieldName || menuName}`}
                    </h2>
                    <p className="text-[11px] font-semibold text-slate-500">
                      {formMode === 'create'
                        ? `Add a new search ${menuName.toLowerCase()}`
                        : 'Update the details of this search field'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => !saving && setFormOpen(false)}
                  className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-slate-200"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-4 px-6 py-5 md:grid-cols-2">
                <DarkField label="Field Name *">
                  <input
                    type="text"
                    value={values.fieldName}
                    onChange={handleInputChange('fieldName')}
                    placeholder="Enter the field name"
                    className={fieldClass}
                  />
                </DarkField>

                <DarkField label="Field Label">
                  <input
                    type="text"
                    value={values.label}
                    onChange={handleInputChange('label')}
                    placeholder="Enter the display label"
                    className={fieldClass}
                  />
                </DarkField>

                <DarkField label="Tier">
                  <input
                    type="text"
                    value={values.tier}
                    onChange={handleInputChange('tier')}
                    placeholder="Enter the tier"
                    className={fieldClass}
                  />
                </DarkField>

                <DarkField label="Filter Type">
                  <select
                    value={values.filterType}
                    onChange={(e) => handleValueChange('filterType')(e.target.value)}
                    className={fieldClass}
                  >
                    <option value="" className="bg-slate-900 text-slate-100">
                      Select filter type
                    </option>
                    {FILTER_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value} className="bg-slate-900 text-slate-100">
                        {option.label}
                      </option>
                    ))}
                  </select>
                </DarkField>

                <DarkField label="Display Order">
                  <input
                    type="number"
                    value={values.displayOrder?.toString() || ''}
                    onChange={handleInputChange('displayOrder')}
                    placeholder="0"
                    className={fieldClass}
                  />
                </DarkField>

                <DarkField label="Filter Status">
                  <select
                    value={values.enabled ? 'true' : 'false'}
                    onChange={(e) => handleValueChange('enabled')(e.target.value === 'true')}
                    className={fieldClass}
                  >
                    <option value="true" className="bg-slate-900 text-slate-100">
                      Active
                    </option>
                    <option value="false" className="bg-slate-900 text-slate-100">
                      Inactive
                    </option>
                  </select>
                </DarkField>
              </div>

              <div className="flex items-center justify-end gap-3 border-t border-slate-800 px-6 py-4">
                <button
                  type="button"
                  onClick={() => setFormOpen(false)}
                  disabled={saving}
                  className="rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-400 transition hover:text-slate-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={saving}
                  className="flex items-center gap-2 rounded-xl bg-cyan-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-400 disabled:opacity-50"
                >
                  {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                  {saving ? 'Saving' : formMode === 'create' ? `Create ${menuName}` : 'Save Changes'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete confirmation */}
      <AnimatePresence>
        {deleteTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
            onClick={() => !deleting && setDeleteTarget(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl"
            >
              <div className="flex items-start gap-4">
                <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-400">
                  <AlertTriangle size={20} />
                </span>
                <div>
                  <h3 className="text-base font-black tracking-tight text-slate-100">Delete {menuName}?</h3>
                  <p className="mt-1 text-sm text-slate-400">
                    This will permanently remove{' '}
                    <span className="font-bold text-slate-200">"{deleteTarget.fieldName}"</span>. This action cannot
                    be undone.
                  </p>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                  className="rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-400 transition hover:text-slate-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex items-center gap-2 rounded-xl bg-rose-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-white transition hover:bg-rose-400 disabled:opacity-50"
                >
                  {deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                  {deleting ? 'Deleting' : 'Delete'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SearchFilterConfigComponent;
